import React from 'react';
import { useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { PlusIcon } from '../../../../../assets/icons';
import { WidgetListDialog } from '../../../../../components/Dialogs/WidgetList';
import styles from './styles';
import { PieChartContent } from './PieChart';
import { Collapse } from './Collapse';
import { TextRow } from './Row';
import { Delete, DragIndicator } from '@mui/icons-material';
import { BarChartContent } from './BarChart';
import { HistoryChart } from './HistoryChart';
import { DocumentList } from './DocumentList';
import { RichText } from './RichText/RichText';
import { Table } from './Table';
import { AmChart } from './AmChart';
import { Blocks } from './Blocks';
import { RiskIndicator } from './RiskIndicator';
import { Spacer } from './Spacer';
import { CustomPortfolio } from './CustomPortfolio';
import { LayoutRow } from './LayoutRow';
import {useAlertContext} from '../../../../../components/Alert/alertContext';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export type WidgetProps = {
  id: number;
  isin?: string;
  type: string;
  content: any;
  originalContent?: any;
  preview: boolean;
  isTop?: boolean;
  themeColor?: string;
  onChange: (id: number | undefined, contentData: any) => void;
  onDelete?: (id: number) => void;
  /** Sibling widgets on the tab (for adopting into a layout row column). */
  tabWidgets?: any[];
  /** Atomically move a top-level tab widget into a row column. */
  onAdoptTabWidgetIntoColumn?: (rowId: number, columnIndex: number, widgetId: number) => void;
  /** Atomically move a column widget back to the tab (after the row). */
  onEjectColumnWidgetToTab?: (rowId: number, columnIndex: number) => void;
}

export const ContentWidget = ({
  id,
  isin,
  type,
  isTop,
  content,
  originalContent,
  preview,
  themeColor,
  onChange,
  onDelete,
  tabWidgets,
  onAdoptTabWidgetIntoColumn,
  onEjectColumnWidgetToTab,
}: any) => {
  const { activeAlert } = useAlertContext();
  const [openDialog, setOpenDialog] = useState(false);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleSelect = (selected) => {
    onChange(id, [...content, selected]);
  };

  const buildContent = (widget: WidgetProps) => {
    switch (widget.type) {
      case 'container': {
        const handleChangeValue = (widgetId, newContentData) => {
          onChange(widget.id, [...content.map((w) => w.id === widgetId ? { ...w, content: newContentData } : w)]);
        };

        const handleDeleteWidget = (widgetId) => {
          activeAlert({
            type: 'info',
            text: "Are you sure to remove this widget?",
          }, () => {
            onChange(widget.id, [...(content?.filter((item) => item.id !== widgetId) || [])]);
          });
        };

        const handleAdoptTabWidgetIntoColumn = (rowId: number, columnIndex: number, widgetId: number) => {
          const adopted = content.find((w) => w.id === widgetId);
          if (!adopted) return;
          const next = content
            .filter((w) => w.id !== widgetId)
            .map((w) => {
              if (w.id !== rowId || w.type !== 'row') return w;
              const cols = Array.isArray(w.content?.columns) ? [...w.content.columns] : [];
              while (cols.length <= columnIndex) {
                cols.push({ width: 50, widget: null });
              }
              cols[columnIndex] = {
                width:
                  typeof cols[columnIndex]?.width === 'number' ? cols[columnIndex].width : 50,
                widget: adopted,
              };
              return {
                ...w,
                content: {
                  gap: typeof w.content?.gap === 'number' ? w.content.gap : 24,
                  columns: cols,
                },
              };
            });
          onChange(widget.id, next);
        };

        const handleEjectColumnWidgetToTab = (rowId: number, columnIndex: number) => {
          const rowWidget = content.find((w) => w.id === rowId && w.type === 'row');
          const ejected = rowWidget?.content?.columns?.[columnIndex]?.widget;
          if (!ejected) return;

          const next: any[] = [];
          for (const w of content) {
            if (w.id !== rowId || w.type !== 'row') {
              next.push(w);
              continue;
            }
            const cols = Array.isArray(w.content?.columns) ? [...w.content.columns] : [];
            if (cols[columnIndex]) {
              cols[columnIndex] = {
                ...cols[columnIndex],
                widget: null,
              };
            }
            next.push({
              ...w,
              content: {
                gap: typeof w.content?.gap === 'number' ? w.content.gap : 24,
                columns: cols,
              },
            });
            // Place the widget right after the row on the tab.
            next.push(ejected);
          }
          onChange(widget.id, next);
        };

        const onDragComplete = (result) => {
          if (!result.destination) return;

          const arr = [...content];

          const removedItem = arr.splice(result.source.index, 1)[0];
          arr.splice(result.destination.index, 0, removedItem);
          onChange(widget.id, arr);
        };

        // @ts-ignore
        return (
          <>
            {isTop ? (
              <Box>
                <DragDropContext onDragEnd={onDragComplete}>
                  <Droppable droppableId="content-widgets">
                    {(provided, snapshot) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {content?.map((w, index) => (
                          <Draggable
                            key={w.id}
                            draggableId={w.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Box
                                sx={{ position: 'relative' }}
                                {...provided.draggableProps}
                                ref={provided.innerRef}
                              >
                                <ContentWidget
                                  {...w}
                                  originalContent={originalContent?.[index]}
                                  isin={isin}
                                  themeColor={themeColor}
                                  preview={preview}
                                  onChange={handleChangeValue}
                                  onDelete={handleDeleteWidget}
                                  tabWidgets={content}
                                  onAdoptTabWidgetIntoColumn={handleAdoptTabWidgetIntoColumn}
                                  onEjectColumnWidgetToTab={handleEjectColumnWidgetToTab}
                                />
                                <Box sx={styles.dragIndicator} className="drag-handle" {...provided.dragHandleProps}>
                                  <DragIndicator />
                                </Box>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
            ) : content?.map((w, index) => (
              <React.Fragment key={index}>
                <ContentWidget
                  {...w}
                  originalContent={originalContent?.[index]}
                  isin={isin}
                  themeColor={themeColor}
                  preview={preview}
                  onChange={handleChangeValue}
                  onDelete={handleDeleteWidget}
                  tabWidgets={content}
                  onAdoptTabWidgetIntoColumn={handleAdoptTabWidgetIntoColumn}
                  onEjectColumnWidgetToTab={handleEjectColumnWidgetToTab}
                />
              </React.Fragment>
            ))}
            {!preview && (
              <Button variant="outlined" sx={styles.widgetButton} startIcon={<PlusIcon />} onClick={handleOpenDialog}>
                Add
              </Button>
            )}

            {//@ts-ignore
              !preview && !!onDelete && (<Button variant="outlined" sx={styles.widgetButton} startIcon={<Delete />} onClick={onDelete && onDelete(widget.id)}>
                Delete
              </Button>
            )}

            {openDialog && (
              <WidgetListDialog
                open={openDialog}
                onSelect={handleSelect}
                onClose={handleCloseDialog}
              />
            )}
          </>
        );
      }

      case 'collapse': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };

        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <Collapse id={id} preview={preview} themeColor={themeColor} originalContent={originalContent?.content} content={content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'pie-chart': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <PieChartContent id={widget?.id} preview={preview} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'block': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <Blocks id={widget?.id} preview={preview} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'bar-chart': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <BarChartContent id={widget?.id} preview={preview} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'spacer': {
        const handleChange = (id, value) => {
          onChange(id, value);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <Spacer id={widget?.id} preview={preview} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'risk-indicator': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <RiskIndicator id={widget?.id} preview={preview} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'text-row': {
        const handleChange = (contentValue) => {
          onChange(widget?.id, contentValue);
        };

        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <TextRow id={widget?.id} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'history': {
        const handleChangeText = (text: string) => {
          onChange(widget?.id, { ...content, text });
        };

        const handleChangeChartType = (type: string) => {
          onChange(widget?.id, { ...content, chartType: type });
        };

        const handleChangeChartIsin = (isin: string) => {
          onChange(widget?.id, { ...content, isin });
        };

        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <HistoryChart
              id={widget?.id}
              isin={isin as string}
              content={content}
              themeColor={themeColor}
              onChange={onChange}
              onChangeDescription={handleChangeText}
              onChangeChartType={handleChangeChartType}
              onChangeChartIsin={handleChangeChartIsin}
            />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'document-list': {
        const handleChangeDocumentList = (id, content) => {
          onChange(id, content);
        };

        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <DocumentList id={widget?.id} content={widget.content} onChange={handleChangeDocumentList} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'rich-text': {
        const handleChangeRichText = (e) => {
          onChange(widget?.id, e);
        }

        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <RichText content={widget.content} onChange={handleChangeRichText} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'table': {
        const handleChangeTable = (value) => {
          console.log('vvvvvvv', value);
          onChange(widget?.id, value ? value : widget.content);
        };

        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <Table isin={isin as string} preview={preview} content={widget.content} originalContent={originalContent} onChange={handleChangeTable} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'amchart': {
        return (
          <Box>
            <AmChart />
          </Box>
        )
      }

      case 'custom-portfolio': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <CustomPortfolio id={widget?.id} preview={preview} content={widget.content} onChange={handleChange} />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      case 'row': {
        const handleChange = (id, contentValue) => {
          onChange(id, contentValue);
        };
        return (
          <Box sx={(theme) => styles.widget(theme, { preview })}>
            <LayoutRow
              id={widget?.id}
              isin={isin}
              preview={preview}
              themeColor={themeColor}
              originalContent={originalContent?.content}
              content={widget.content}
              onChange={handleChange}
              tabWidgets={tabWidgets}
              onAdoptTabWidgetIntoColumn={onAdoptTabWidgetIntoColumn}
              onEjectColumnWidgetToTab={onEjectColumnWidgetToTab}
            />
            <IconButton sx={styles.remove} onClick={() => onDelete && onDelete(widget.id)}>
              <Delete />
            </IconButton>
          </Box>
        )
      }

      default:
        return (
          <></>
        )
    }
  };

  return buildContent({
    id,
    isin,
    type,
    preview,
    content,
    originalContent,
    onChange
  });
};
